"use client";
import cn from "classnames";
import React, {
  useRef,
  useEffect,
  useMemo,
  useLayoutEffect,
  HTMLAttributes,
  forwardRef,
} from "react";
import { useMemoizedFn } from "ahooks";
import throttle from "lodash-es/throttle";
import { useParams } from "next/navigation";
// import { deviceDetector } from "@ospweb/sdk-ui";
import { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { isTableHomePath } from "../maintabs/isHomePath";
import { usePathname } from "@hooks/usePathname";

const STORAGE_KEY_PREFIX = "scroll_position_";

/**
 * 滚动容器类型
 */
type ScrollContainer = HTMLElement | Window;

/**
 * 滚动容器查找选项
 */
interface ScrollContainerFindOptions {
  /** 是否检查元素本身 */
  checkSelf?: boolean;
  /** 是否向上查找父级滚动容器 */
  checkParents?: boolean;
  /** 是否向下查找 Shadow DOM */
  checkShadowDOM?: boolean;
  /** 是否支持窗口滚动 */
  checkWindow?: boolean;
  /** 最大向上查找深度 */
  maxParentDepth?: number;
  /** 是否使用缓存 */
  useCache?: boolean;
}

/**
 * 滚动容器查找结果缓存
 * 使用 WeakMap 避免内存泄漏
 */
const scrollContainerCache = new WeakMap<HTMLElement, ScrollContainer | null>();

/**
 * 判断元素是否可滚动（优化版）
 * 使用更轻量的检查方式，避免不必要的 getComputedStyle
 */
function isScrollableElementFast(element: HTMLElement): boolean {
  // 快速检查：先检查 scrollHeight 和 clientHeight
  // 如果不可滚动，就不需要检查样式
  if (element.scrollHeight <= element.clientHeight) {
    return false;
  }

  // 只有在可能滚动时才检查样式
  const style = window.getComputedStyle(element);
  const hasOverflow =
    style.overflowY === "auto" ||
    style.overflowY === "scroll" ||
    style.overflow === "auto" ||
    style.overflow === "scroll";

  return hasOverflow;
}

/**
 * 在 Shadow DOM 中查找滚动容器（优化版）
 * 限制查找范围，避免遍历所有元素
 */
function findScrollableInShadowDOMOptimized(
  shadowRoot: ShadowRoot,
): HTMLElement | null {
  // 优先查找常见的滚动容器选择器
  const commonSelectors = [
    '[style*="overflow"]',
    '[class*="scroll"]',
    '[class*="overflow"]',
  ];

  for (const selector of commonSelectors) {
    try {
      const elements = shadowRoot.querySelectorAll<HTMLElement>(selector);
      for (const el of elements) {
        if (isScrollableElementFast(el)) {
          return el;
        }
      }
    } catch (e) {
      // 忽略选择器错误
      continue;
    }
  }

  // 如果没找到，再遍历所有元素（限制深度）
  try {
    const allElements = shadowRoot.querySelectorAll<HTMLElement>("*");
    let count = 0;
    for (const el of allElements) {
      if (count++ > 100) {
        // 限制最多检查 100 个元素
        break;
      }
      if (isScrollableElementFast(el)) {
        return el;
      }
    }
  } catch (e) {
    // 忽略查询错误
  }

  return null;
}

/**
 * 智能查找滚动容器（性能优化版）
 * 特点：
 * 1. 使用缓存避免重复计算
 * 2. 快速路径优先
 * 3. 限制查找深度
 * 4. 延迟查找 Shadow DOM
 */
function findScrollContainerOptimized(
  element: HTMLElement | null,
  options: ScrollContainerFindOptions = {},
): ScrollContainer | null {
  const {
    checkSelf = true,
    checkParents = true,
    checkShadowDOM = true,
    checkWindow = false,
    maxParentDepth = 10,
    useCache = true,
  } = options;

  if (!element) {
    return checkWindow ? window : null;
  }

  // 1. 检查缓存
  if (useCache && scrollContainerCache.has(element)) {
    const cached = scrollContainerCache.get(element);
    if (cached !== undefined) {
      return cached;
    }
  }

  // 2. 快速路径：检查元素本身（最常见的情况）
  if (checkSelf) {
    if (isScrollableElementFast(element)) {
      if (useCache) {
        scrollContainerCache.set(element, element);
      }
      return element;
    }
  }

  // 3. 向上查找父级（限制深度）
  if (checkParents) {
    let parent = element.parentElement;
    let depth = 0;
    while (parent && depth < maxParentDepth) {
      if (isScrollableElementFast(parent)) {
        if (useCache) {
          scrollContainerCache.set(element, parent);
        }
        return parent;
      }
      if (parent === document.documentElement || parent === document.body) {
        break;
      }
      parent = parent.parentElement;
      depth++;
    }
  }

  // 4. Shadow DOM 查找（延迟执行，使用 requestAnimationFrame）
  if (checkShadowDOM && element.shadowRoot) {
    const shadowScrollable = findScrollableInShadowDOMOptimized(
      element.shadowRoot,
    );
    if (shadowScrollable) {
      if (useCache) {
        scrollContainerCache.set(element, shadowScrollable);
      }
      return shadowScrollable;
    }
  }

  // 5. 窗口滚动
  const result = checkWindow ? window : null;
  if (useCache) {
    scrollContainerCache.set(element, result);
  }
  return result;
}

/**
 * 清除缓存（在元素被移除时调用）
 */
function clearScrollContainerCache(element: HTMLElement): void {
  scrollContainerCache.delete(element);
}

/**
 * 获取存储 key（按 pathname + device 区分，避免 PC/移动端共用同一 key）
 */
const getStorageKey = (pathname: string, device?: string): string => {
  const suffix = device ? `__${device}` : "";
  return `${STORAGE_KEY_PREFIX}${pathname}${suffix}`;
};

/**
 * 保存滚动位置到 sessionStorage
 */
const saveScrollPosition = (
  pathname: string,
  scrollTop: number,
  device?: string,
): void => {
  if (scrollTop === undefined || scrollTop === null) {
    return;
  }
  const key = getStorageKey(pathname, device);
  sessionStorage.setItem(key, String(scrollTop));
};

/**
 * 从 sessionStorage 恢复滚动位置
 */
const restoreScrollPosition = (
  container: ScrollContainer | null,
  pathname: string,
  device?: string,
): number | null => {
  if (!container) {
    return null;
  }
  const key = getStorageKey(pathname, device);
  const savedPosition = sessionStorage.getItem(key);
  if (savedPosition) {
    const scrollTop = parseFloat(savedPosition);
    if (!isNaN(scrollTop) && scrollTop >= 0) {
      return scrollTop;
    }
  }
  return null;
};

/**
 * 获取滚动位置（统一接口）
 */
function getScrollTop(container: ScrollContainer): number {
  if (container === window) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  return (container as HTMLElement).scrollTop;
}

/**
 * 设置滚动位置（统一接口）
 */
function setScrollTop(container: ScrollContainer, scrollTop: number): void {
  if (container === window) {
    window.scrollTo({
      top: scrollTop,
      behavior: "instant",
    });
  } else {
    const element = container as HTMLElement;
    if (typeof element.scrollTo === "function") {
      try {
        element.scrollTo({
          top: scrollTop,
          behavior: "instant",
        });
      } catch (e) {
        element.scrollTop = scrollTop;
      }
    } else {
      element.scrollTop = scrollTop;
    }
  }
}

export interface RestorerViewObserverProps {
  /** 元素引用，用于查找滚动容器 */
  ref: React.RefObject<HTMLElement | null>;
  /** 直接指定滚动容器（如果提供，将跳过自动查找） */
  scrollContainer?: React.RefObject<ScrollContainer | null>;
  /** 滚动容器查找选项 */
  findOptions?: ScrollContainerFindOptions;
}

const RestorerViewObserver = ({
  ref,
  scrollContainer,
  findOptions,
}: RestorerViewObserverProps) => {
  const pathname = usePathname();
  const { device } = useParams<{ device?: DeviceType }>();
  const actualScrollContainerRef = useRef<ScrollContainer | null>(null);

  const isMountedRef = useRef(false);
  const isRestoringRef = useRef(false);
  const hasScrolledRef = useRef(false);
  const restoreTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 恢复保护定时器

  // 查找或使用指定的滚动容器
  useEffect(() => {
    if (scrollContainer?.current) {
      // 如果直接指定了滚动容器，使用它
      actualScrollContainerRef.current = scrollContainer.current;
      return;
    }

    // 否则，智能查找滚动容器
    if (ref.current) {
      const foundContainer = findScrollContainerOptimized(ref.current, {
        checkSelf: true,
        checkParents: true,
        checkShadowDOM: true,
        checkWindow: false,
        maxParentDepth: 10,
        useCache: true,
        ...findOptions,
      });

      if (foundContainer) {
        actualScrollContainerRef.current = foundContainer;
      }
    }

    // 清理函数：清除缓存
    return () => {
      if (ref.current) {
        clearScrollContainerCache(ref.current);
      }
    };
  }, [ref, scrollContainer, findOptions]);

  // 记录是否滚动过（通过滚动事件处理，这里不需要检查）

  // 节流保存滚动位置（每 200ms 最多保存一次），按 device 区分 key
  const throttledSave = useMemo(
    () =>
      throttle((path: string, top: number | undefined, deviceKey?: string) => {
        if (
          !isRestoringRef.current &&
          top !== undefined &&
          hasScrolledRef.current
        ) {
          saveScrollPosition(path, top, deviceKey);
        }
      }, 200),
    [],
  );

  /**
   * 恢复滚动位置
   * 立即滚动到保存的位置，使用 scrollTo 方法确保滚动生效
   */
  const restorePosition = useMemoizedFn(() => {
    const container =
      actualScrollContainerRef.current || scrollContainer?.current;
    if (!container) {
      return;
    }

    const savedScrollTop = restoreScrollPosition(container, pathname, device);

    if (savedScrollTop !== null) {
      // 清除之前的恢复保护定时器
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }

      // 标记正在恢复，立即禁用保存
      isRestoringRef.current = true;

      // 使用统一的滚动接口
      setScrollTop(container, savedScrollTop);

      // 恢复后等待一段时间再允许保存（300ms）
      // 这样可以确保恢复操作完全完成，避免恢复操作触发保存
      restoreTimeoutRef.current = setTimeout(() => {
        isRestoringRef.current = false;
        restoreTimeoutRef.current = null;
      }, 300);
    }
  });

  // 监听滚动事件并保存位置
  useEffect(() => {
    const container =
      actualScrollContainerRef.current || scrollContainer?.current;
    if (!container) {
      return;
    }

    // 处理滚动事件的函数
    const handleScroll = () => {
      if (!isMountedRef.current || isRestoringRef.current) {
        return;
      }

      const currentScrollTop = getScrollTop(container);

      // 记录是否滚动过
      if (!hasScrolledRef.current && currentScrollTop > 0) {
        hasScrolledRef.current = true;
      }

      if (hasScrolledRef.current && currentScrollTop !== undefined) {
        throttledSave(pathname, currentScrollTop, device);
      }
    };

    // 根据容器类型添加事件监听
    if (container === window) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      const element = container as HTMLElement;
      element.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      if (container === window) {
        window.removeEventListener("scroll", handleScroll);
      } else {
        (container as HTMLElement).removeEventListener("scroll", handleScroll);
      }
    };
  }, [pathname, throttledSave, scrollContainer]);

  // ========== 场景 1: 组件挂载 ==========
  useLayoutEffect(() => {
    // 只在首次挂载时执行
    if (isMountedRef.current) {
      return;
    }

    // tab 页面跳到 main 页面回来，会重新挂载，需要延迟恢复滚动位置
    // 使用更长的延迟确保 DOM 完全渲染完成
    const mountTimeoutId = setTimeout(() => {
      restorePosition();
      isMountedRef.current = true;
    }, 0);

    return () => {
      clearTimeout(mountTimeoutId);
      // 清理恢复保护定时器
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
      isMountedRef.current = false;
      isRestoringRef.current = false;
    };
  }, [restorePosition]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      // 清理恢复保护定时器
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
      // 清理节流函数
      throttledSave.cancel();
      // 重置状态
      isMountedRef.current = false;
      isRestoringRef.current = false;
    };
  }, [throttledSave]);

  return null;
};

export const ScrollRestorerViewObserver = ({
  ref,
  scrollContainer,
  findOptions,
}: RestorerViewObserverProps) => {
  const pathname = usePathname();
  const isHomePath = useMemo(() => {
    return isTableHomePath(pathname);
  }, [pathname]);

  if (!isHomePath) {
    return null;
  }

  // 只在移动端显示
  // if (!deviceDetector.isMobile()) {
  //   return null;
  // }

  return (
    <RestorerViewObserver
      key={pathname}
      ref={ref}
      scrollContainer={scrollContainer}
      findOptions={{
        checkSelf: true, // 检查元素本身（最常见情况）
        checkParents: true, // 检查父级滚动容器
        checkShadowDOM: true, // 检查 Shadow DOM（用于 Web Components）
        useCache: true, // 启用缓存优化性能
        ...findOptions,
      }}
    />
  );
};

/**
 * 可滚动视图组件，支持保存和恢复滚动位置
 *
 * 功能特性：
 * - 自动检测滚动容器（元素本身、父级、Shadow DOM）
 * - 使用缓存机制优化性能
 * - 支持 ref 转发，允许父组件访问 DOM 元素
 *
 * 使用方式：
 * 1. 标准用法：元素本身有 overflow-y-auto/scroll
 *    <RestorerScrollableView className="overflow-y-auto">
 *      {children}
 *    </RestorerScrollableView>
 *
 * 2. 带 ref 转发：
 *    <RestorerScrollableView ref={containerRef} className="overflow-y-auto">
 *      {children}
 *    </RestorerScrollableView>
 *
 * 注意：组件会自动检测滚动容器，无需手动指定
 */
export const ScrollRestorerScrollableView = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ children, className, style, ...restProps }, forwardedRef) => {
  const internalRef = useRef<HTMLDivElement>(null);

  // 使用回调 ref 同时更新外部 ref 和内部 ref
  const setRef = useMemoizedFn((element: HTMLDivElement | null) => {
    // 更新内部 ref（用于 RestorerViewObserver）
    internalRef.current = element;

    // 更新外部 ref
    if (typeof forwardedRef === "function") {
      forwardedRef(element);
    } else if (forwardedRef) {
      (forwardedRef as React.RefObject<HTMLDivElement | null>).current =
        element;
    }
  });

  return (
    <div ref={setRef} className={cn(className)} style={style} {...restProps}>
      {children}
      {/* 自动检测滚动容器：优先检查元素本身，然后检查父级和 Shadow DOM */}
      <ScrollRestorerViewObserver ref={internalRef} />
    </div>
  );
});

ScrollRestorerScrollableView.displayName = "RestorerScrollableView";
